/**
 * Renders the chat input area, including the text area, send button, and attachment handling.
 *
 * Modern chat input implementation following patterns from ChatGPT, Discord, and Slack.
 * Features auto-resizing textarea, inline attachment display, and drag-and-drop support.
 * This component uses signal-based state management via ChatInputState instead of React hooks.
 */

import type { ChatAttachment } from '@elara/client-core/states/ChatInputState';

import type { IconName } from '#ui/MaterialIcon.js';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { DATA_ATTACHMENT_ICONS, ROUTES } from '@elara/client-core/constants';
import { assetPreviewUrl, fileToAsset } from '@elara/client-core/lib/asset';
import { cn, pluralize } from '@elara/client-core/lib/utils';
import { isAssetAttachment, isDataAttachment, isUrlAttachment } from '@elara/client-core/states/ChatInputState';
import { shorten } from '@elara/core/utils';

import { getFileTypeIcon } from '#lib/file-utils.js';
import { useChatInputState, useChatsState, useMessageViewState, useUIState } from '#states/hooks.js';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  AppImage,
  Button,
  Card,
  IconSizes,
  MaterialIcon,
  Textarea,
} from '#ui';
import { AskUserWidget } from '#ui/chat/AskUserWidget.js';
import { CollapsibleBlock } from '#ui/debug/collapsible-block.jsx';
import { ChatNotificationOffer } from '#views/chat/ChatNotificationOffer.js';
import { PendingPreferenceProposals } from '#views/chat/PendingPreferenceProposals.js';
import { LinkButton } from '#views/Router.js';

/** The floating slot above the composer, inset to the card's edges, shared by the composer prompts. */
const COMPOSER_PROMPT_CLASS = 'absolute bottom-full inset-x-4 md:inset-x-6 mb-2 z-20';

export function ChatInputView() {
  const uiState = useUIState();
  const chatInput = useChatInputState();
  const chatsState = useChatsState();
  const messageView = useMessageViewState();
  const textareaRef = useRef<HTMLTextAreaElement | undefined>(undefined);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      // Convert dropped web Files to neutral assets at the producer boundary.
      chatInput.addAttachments(acceptedFiles.map(fileToAsset));
    },
    noClick: true,
    noKeyboard: true,
    onDragEnter: () => chatInput.setDragging(true),
    onDragLeave: () => chatInput.setDragging(false),
    onDropAccepted: () => chatInput.setDragging(false),
    onDropRejected: () => chatInput.setDragging(false),
  });

  async function handleSend() {
    await chatInput.sendMessage();
    textareaRef.current?.focus();
  }

  async function handleStop() {
    const restoredText = await chatsState.stopStreaming();
    if (restoredText !== undefined && !chatInput.hasUnsavedChanges) {
      chatInput.restoreEarlyStoppedDraft(restoredText);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    chatInput.handleEnterKey(e.nativeEvent).catch((err) => {
      console.error('Error handling Enter key:', err);
    });
  }

  function resizeTextarea() {
    if (textareaRef.current) {
      const target = textareaRef.current;
      target.style.height = 'auto';
      target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
    }
  }

  // Read signals directly in render
  const {
    attachments,
    sendError,
    attachmentsExceedLimit,
    formattedAttachmentSize,
    isDragging = isDragActive,
    supportsVoiceInput,
    isVoiceInputActive,
    displayText,
  } = chatInput;

  // Object URLs for blob-backed image previews, created once per attachments
  // change (not on every render). Revoked on cleanup below so they never leak
  // — covering load failures, removal-before-load, and unmount-during-load,
  // which an onLoad-only revoke would miss.
  const previewUrls = useMemo(() => {
    const urls = new Map<ChatAttachment, string>();
    for (const attachment of attachments) {
      if (isAssetAttachment(attachment) && attachment.mimeType.startsWith('image/')) {
        urls.set(attachment, assetPreviewUrl(attachment));
      }
    }
    return urls;
  }, [attachments]);

  useEffect(() => {
    return () => {
      for (const url of previewUrls.values()) {
        if (url.startsWith('blob:')) URL.revokeObjectURL(url);
      }
    };
  }, [previewUrls]);

  // Get platform-specific keyboard shortcut
  const isMac = navigator.platform.toUpperCase().includes('MAC');
  const keyboardShortcut = isMac ? '⌘⇧V' : 'Ctrl+Shift+V';

  // Register textarea ref with ChatInputState for external focus control
  // Using a callback ref to ensure we always have the latest ref
  const textareaRefCallback = useCallback(
    (element: HTMLTextAreaElement | null) => {
      textareaRef.current = element ?? undefined;
      chatInput.registerTextareaRef(element ?? undefined);
    },
    [chatInput],
  );

  // Resize textarea when displayText changes (including voice input)
  useEffect(() => {
    resizeTextarea();
  }, [displayText]);

  // Resize textarea when layout changes (e.g., tools panel opens/closes)
  useEffect(() => {
    // Resize immediately on mount
    resizeTextarea();

    // Debounce resize calls to prevent ResizeObserver loops
    let resizeTimeout: NodeJS.Timeout;

    // Set up ResizeObserver to detect layout changes
    const resizeObserver = new ResizeObserver(() => {
      // Clear any pending resize
      clearTimeout(resizeTimeout);

      // Debounce the resize call to prevent loops
      resizeTimeout = setTimeout(() => {
        try {
          resizeTextarea();
        } catch (error) {
          // Silently handle ResizeObserver loop errors
          console.debug('ResizeObserver callback error (handled):', error);
        }
      }, 16); // ~60fps
    });

    // Observe the textarea's parent container for layout changes
    if (textareaRef.current?.parentElement) {
      resizeObserver.observe(textareaRef.current.parentElement);
    }

    return () => {
      clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
    };
  }, []);

  // Focus textarea when voice input starts
  useEffect(() => {
    if (isVoiceInputActive) {
      textareaRef.current?.focus();
    }
  }, [isVoiceInputActive]);

  // Handle keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const isCmdOrCtrl = e.metaKey || e.ctrlKey;

      if (isCmdOrCtrl && e.shiftKey && (e.key === 'V' || e.key === 'v')) {
        e.preventDefault();

        if (chatInput.supportsVoiceInput && !chatInput.isInputDisabled) {
          chatInput.toggleVoiceInput();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [chatInput]);

  const questionCount = chatInput.questions.length;
  const answeredQuestionCount = Object.keys(chatInput.answeredQuestions).length;

  const questionsIcon: IconName = useMemo(() => {
    if (answeredQuestionCount === questionCount) {
      return 'check_circle';
    }
    if (answeredQuestionCount > 0) {
      return 'radio_button_partial';
    }
    return 'radio_button_unchecked';
  }, [answeredQuestionCount, questionCount]);

  const questionBoxTitle = useMemo(() => {
    if (answeredQuestionCount === questionCount) {
      return 'All questions answered';
    }
    if (answeredQuestionCount > 0) {
      return `Answered ${answeredQuestionCount} of ${questionCount} ${pluralize(questionCount, 'question')}`;
    }
    return `${questionCount} ${pluralize(questionCount, 'question')} to answer`;
  }, [answeredQuestionCount, questionCount]);

  // Demo trips are read-only: replace the input with a prompt to plan a real trip.
  if (chatInput.isDemoTrip) {
    return <DemoTripNotice />;
  }

  return (
    <div>
      <div {...getRootProps()} className="relative">
        {/* Container with max width for better readability */}
        <div className="relative max-w-3xl mx-auto px-4 md:px-6">
          {/* "Remember this for future trips?" proposals, asked one at a time. Floats over the bottom of the
              feed (inset to the card's edges) so it doesn't push the composer or shrink the messages area.
              When none are waiting, the same slot can offer notifications for a slow reply. */}
          <PendingPreferenceProposals
            className={COMPOSER_PROMPT_CLASS}
            whenIdle={<ChatNotificationOffer className={COMPOSER_PROMPT_CLASS} />}
          />

          {/* Error message display */}
          {sendError &&
            (sendError.length > 100 ? (
              <CollapsibleBlock
                className="mb-2 p-2 bg-destructive/10 text-destructive text-sm rounded-md border-destructive "
                label={`Failed to send message: ${formatSendErrorTitle(sendError)}`}
              >
                <pre className="text-sm h-96 overflow-auto rounded-md ">{sendError}</pre>
              </CollapsibleBlock>
            ) : (
              <div className="mb-2 p-2 bg-destructive/10 text-destructive text-sm rounded-md border-destructive ">
                {`Failed to send message: ${sendError}`}
              </div>
            ))}

          {/* Main input card */}
          <Card
            className={cn(
              'max-h-[60dvh] flex flex-col transition-all rounded-lg bg-background border border-transparent overflow-hidden',
              isDragging && 'ring-2 ring-primary border-primary',
              !chatInput.isInputDisabled && 'shadow-sm',
            )}
          >
            {/* Ask user widget */}
            {questionCount > 0 && !chatInput.isInputDisabled && (
              <div className="border-b flex overflow-hidden">
                <Accordion type="single" collapsible defaultValue="questions" className="w-full flex">
                  <AccordionItem value="questions" className="flex flex-col w-full">
                    <AccordionTrigger className="text-sm px-4 py-3 m-0 hover:bg-secondary rounded-none cursor-pointer">
                      <div className="flex items-center gap-2">
                        <MaterialIcon icon={questionsIcon} size={IconSizes.sm} />
                        <span>{questionBoxTitle}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="p-4 pt-3 overflow-y-auto">
                      <AskUserWidget questions={chatInput.questions} interactive={true} />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            )}

            {/* Attachments display - integrated within the card */}
            {attachments.length > 0 && (
              <div className="px-4 pt-4">
                <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                  {attachments.map((attachment, index) => {
                    const isUrl = isUrlAttachment(attachment);
                    const isData = isDataAttachment(attachment);

                    // Handle data attachments (destination, hotel, etc.)
                    if (isData) {
                      const icon = DATA_ATTACHMENT_ICONS[attachment.type] || ('attachment' as IconName);
                      const typeLabel = attachment.type.charAt(0).toUpperCase() + attachment.type.slice(1);

                      return (
                        <div key={index} className="flex items-center gap-2 bg-muted rounded-md px-2 py-1.5 text-sm">
                          <MaterialIcon icon={icon} className="flex-shrink-0 text-primary" />
                          <div className="min-w-0">
                            <div className="truncate max-w-[200px] font-medium">
                              {attachment.displayName || typeLabel}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 flex-shrink-0"
                            onClick={() => chatInput.removeAttachment(index)}
                          >
                            <MaterialIcon icon="close" size={IconSizes.xs} />
                          </Button>
                        </div>
                      );
                    }

                    // Handle asset/URL attachments. Both expose name + mimeType,
                    // so only the preview URL source differs by attachment kind.
                    const mimeType = attachment.mimeType;
                    const name = attachment.name;
                    const isImage = mimeType.startsWith('image/');
                    const { iconName, className: iconClassName } = getFileTypeIcon(mimeType, name);
                    // Reuse the memoized object URL so it isn't recreated each
                    // render; `previewUrls` owns its lifecycle (revoked on cleanup).
                    const imageUrl = isUrl
                      ? attachment.url
                      : (previewUrls.get(attachment) ?? assetPreviewUrl(attachment));
                    const size = attachment.size ?? 0;

                    return (
                      <div key={index} className="flex items-center gap-2 bg-muted rounded-md px-2 py-1.5 text-sm">
                        {isImage ? (
                          <div className="w-8 h-8 rounded overflow-hidden bg-background flex-shrink-0">
                            <AppImage src={imageUrl} alt={name} className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <MaterialIcon icon={iconName} className={cn('flex-shrink-0', iconClassName)} />
                        )}
                        <div className="min-w-0">
                          <div className="truncate max-w-[200px] font-medium">{name}</div>
                          <div className="text-xs text-muted-foreground">
                            {size > 0 ? chatInput.formatFileSize(size) : 'URL'}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 flex-shrink-0"
                          onClick={() => chatInput.removeAttachment(index)}
                        >
                          <MaterialIcon icon="close" size={IconSizes.xs} />
                        </Button>
                      </div>
                    );
                  })}
                </div>
                {/* Attachment size warning */}
                {attachmentsExceedLimit && (
                  <div className="mt-2 text-xs text-destructive">
                    Total size ({formattedAttachmentSize}) exceeds 25MB limit
                  </div>
                )}
              </div>
            )}

            {/* Input area */}
            <div className="flex flex-col gap-1 p-2">
              {/* Hidden file input for dropzone */}
              <input {...getInputProps()} />

              {/* Message input */}
              <div className={cn('relative', chatInput.isInputDisabled && 'opacity-70')}>
                <Textarea
                  ref={textareaRefCallback}
                  value={displayText}
                  onChange={(e) => chatInput.setMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={chatInput.isChatEmpty ? undefined : chatInput.placeholder}
                  disabled={chatInput.isInputDisabled}
                  className="min-h-[36px] max-h-[120px] resize-none border-0 px-2 py-2 text-base sm:text-base bg-transparent shadow-none focus-visible:shadow-none"
                  rows={1}
                  style={{ height: 'auto', overflowY: displayText.split('\n').length > 4 ? 'auto' : 'hidden' }}
                  onInput={resizeTextarea}
                />
                {chatInput.isChatEmpty && !displayText && <CyclingPlaceholder text={chatInput.placeholder} />}
              </div>

              <div className="flex justify-between items-center gap-2">
                <div className={cn('flex justify-start items-center gap-1', chatInput.isInputDisabled && 'opacity-70')}>
                  {/* Attachment button (opens the platform FilePicker port) */}
                  <Button
                    variant="icon"
                    size="icon"
                    className="cursor-pointer"
                    tooltip="Add files"
                    disabled={chatInput.isInputDisabled}
                    onClick={() => void chatInput.pickFiles()}
                    aria-label="Add files"
                  >
                    <MaterialIcon icon="add" />
                  </Button>

                  {/* Voice input button - only show if browser supports it */}
                  {supportsVoiceInput && (
                    <Button
                      variant={isVoiceInputActive ? 'destructive' : 'icon'}
                      size="icon"
                      className="flex-shrink-0 cursor-pointer rounded-full"
                      tooltip={
                        isVoiceInputActive
                          ? `Stop dictating (${keyboardShortcut})`
                          : `Start dictating (${keyboardShortcut})`
                      }
                      onClick={() => chatInput.toggleVoiceInput()}
                      disabled={chatInput.isInputDisabled}
                    >
                      {isVoiceInputActive ? <MaterialIcon icon="stop" filled /> : <MaterialIcon icon="mic" />}
                    </Button>
                  )}
                </div>

                {chatsState.isStreaming ? (
                  <Button
                    variant="icon"
                    size="icon"
                    className="flex-shrink-0 cursor-pointer rounded-full"
                    tooltip={chatsState.isStopping ? 'Stopping response' : 'Stop answering'}
                    onClick={handleStop}
                    aria-label={chatsState.isStopping ? 'Stopping response' : 'Stop answering'}
                    aria-busy={chatsState.isStopping}
                    isBusy={chatsState.isStopping}
                  >
                    <MaterialIcon icon="stop" filled />
                  </Button>
                ) : (
                  <Button
                    variant="icon"
                    size="icon"
                    className={cn(
                      'flex-shrink-0 cursor-pointer text-accent disabled:text-muted-foreground',
                      chatInput.isInputDisabled && 'opacity-70',
                    )}
                    tooltip="Send message"
                    disabled={chatInput.isInputDisabled || !chatInput.canSend}
                    onClick={handleSend}
                    aria-label="Send message"
                    aria-controls="send-message"
                  >
                    <MaterialIcon icon="arrow_upward" />
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </div>
        {/* Drag overlay */}
        {isDragActive && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"></div>}
      </div>
    </div>
  );
}

function formatSendErrorTitle(sendError: string, maxLength = 128) {
  return shorten(sendError.split('\n')[0] ?? 'Unknown error', { maxLength, ellipsis: '...' });
}

/**
 * Shown in place of the chat input when viewing a demo trip. Demo trips are
 * seeded examples, so we steer users toward starting a fresh trip rather than
 * continuing the conversation in the demo.
 */
function DemoTripNotice() {
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 w-full">
      <Card className="flex flex-col sm:flex-row items-center justify-center gap-3 rounded-lg shadow-sm bg-background p-4 text-center sm:text-left">
        <p className="flex-1 text-sm text-muted-foreground text-balance">
          This is a demo trip, so chat is disabled. Plan a new trip to start chatting with Elara.
        </p>
        <LinkButton to={ROUTES.TRIP_NEW} className="flex-shrink-0">
          Plan a new trip
        </LinkButton>
      </Card>
    </div>
  );
}

function CyclingPlaceholder({ text }: { text: string }) {
  const [visible, setVisible] = useState(true);
  const [displayedText, setDisplayedText] = useState(text);

  useEffect(() => {
    if (text === displayedText) return;
    // Fade out, swap text, fade in
    setVisible(false);
    const timeout = setTimeout(() => {
      setDisplayedText(text);
      setVisible(true);
    }, 200);
    return () => clearTimeout(timeout);
  }, [text, displayedText]);

  return (
    <div
      className="absolute left-2 top-2 pointer-events-none text-base text-muted-foreground transition-opacity duration-200"
      style={{ opacity: visible ? 1 : 0 }}
    >
      {displayedText}
    </div>
  );
}
